import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  BookOpen, Sparkles, FileText, Globe, Shield, Calendar,
  Check, ArrowRight, Menu, X, ChevronRight, Heart,
  Users, Presentation, Mail, Search, Crown, Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Guide', to: '/guide' },
  { label: 'About', to: '/about' },
  { label: 'Support', to: '/support' },
];

const FEATURES = [
  { icon: Sparkles, title: 'AI-Powered Generation', desc: 'Create Bible studies, devotionals, curriculum, and sermon notes in minutes with Claude AI.' },
  { icon: BookOpen, title: 'Scripture Integration', desc: 'Built-in Scripture lookup with Greek and Hebrew word studies for deeper theological insight.' },
  { icon: FileText, title: 'Multi-Format Export', desc: 'Export to PDF, Word, and PowerPoint with your church branding and logo.' },
  { icon: Globe, title: 'Multi-Language', desc: 'Generate content in 7 languages to serve diverse congregations worldwide.' },
  { icon: Calendar, title: 'Sermon Planner', desc: 'Plan quarter, half-year, or full-year sermon series with AI-assisted theme development.' },
  { icon: Mail, title: 'Share & Collaborate', desc: 'Email studies directly, share via public links, and build a content library.' },
  { icon: Search, title: 'Archive Search', desc: 'Search across all your saved content, sermon plans, and Bible studies.' },
  { icon: Shield, title: 'Admin Controls', desc: 'Manage users, toggle features, track analytics, and control access by tier.' },
];

const TIER_DATA = {
  starter: { name: 'Starter', price: '4.99', features: ['All Content Types', '50 Generations/mo', 'PDF & Word Export', 'Template Library'] },
  pro: { name: 'Pro', price: '14.99', popular: true, features: ['Unlimited Generations', 'All Exports (PDF, Word, PPTX)', 'Email Sharing', 'Sermon Planner', 'All Templates'] },
  church: { name: 'Church', price: '29.99', features: ['Everything in Pro', 'Up to 5 Team Members', 'Church Scheduling', 'Custom Branding', 'Priority Support'] },
  organization: { name: 'Organization', price: '99.00', features: ['Everything in Church', 'Up to 100 Team Members', 'Dedicated Account Manager', 'Custom Plans Available'] },
};

function InteractiveShowcase({ navigate, user }) {
  const [activeTab, setActiveTab] = useState('compare');
  const [compareInput, setCompareInput] = useState('');
  const [scheduleDay, setScheduleDay] = useState('sunday');
  const [scriptureRef, setScriptureRef] = useState('');

  const TABS = [
    { id: 'compare', label: 'Compare Religions', icon: Globe, color: 'purple' },
    { id: 'schedule', label: 'Church Schedule', icon: Calendar, color: 'green' },
    { id: 'scripture', label: 'Scripture Lookup', icon: BookOpen, color: 'blue' },
  ];

  const DEMO_COMPARE = {
    thought: 'The concept of grace',
    results: [
      { religion: 'Christianity', text: 'Unmerited favor from God through faith in Christ. "For by grace you have been saved through faith" (Eph 2:8)', highlight: true },
      { religion: 'Islam', text: 'Rahma (mercy) flows from Allah to those who submit. Grace is earned through obedience and good deeds.' },
      { religion: 'Buddhism', text: 'No direct equivalent. The concept of compassion (karuna) is closest, arising from enlightened awareness.' },
      { religion: 'Hinduism', text: 'Prasada — divine grace bestowed by God. In Bhakti traditions, grace comes through devotion to a deity.' },
    ],
  };

  const DEMO_SCHEDULE = {
    sunday: [
      { role: 'Opening Prayer', person: 'Deacon Williams' },
      { role: 'Worship Leader', person: 'Sister Grace' },
      { role: 'Scripture Reader', person: 'Brother James' },
      { role: 'Preacher', person: 'Pastor Johnson' },
      { role: 'Closing Prayer', person: 'Elder Thompson' },
    ],
    wednesday: [
      { role: 'Prayer Leader', person: 'Sister Maria' },
      { role: 'Worship', person: 'Youth Team' },
      { role: 'Speaker', person: 'Pastor Johnson' },
      { role: 'Closing', person: 'Deacon Brown' },
    ],
  };

  return (
    <section id="showcase" className="py-20 px-6 bg-[#F4F2EC]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F1A2A] mb-4">See It In Action</h2>
          <p className="text-[#475569] text-base lg:text-lg max-w-2xl mx-auto">Explore how our most popular features work — interactively.</p>
        </div>

        {/* Tab buttons */}
        <div className="flex justify-center gap-2 sm:gap-3 mb-8 flex-wrap" data-testid="showcase-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-[#1B3A6B] text-white shadow-lg shadow-[#1B3A6B]/20' : 'bg-white text-[#475569] hover:text-[#1B3A6B] border border-[#E2DFD8]'}`}
                data-testid={`showcase-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#E2DFD8] overflow-hidden" data-testid="showcase-content">
          {activeTab === 'compare' && (
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0F1A2A]">Compare Religions</h3>
                  <p className="text-xs text-[#94A3B8]">Enter any theological concept to see how it's viewed across world religions</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={compareInput || DEMO_COMPARE.thought}
                    onChange={e => setCompareInput(e.target.value)}
                    placeholder="e.g., The concept of grace, Forgiveness, Prayer..."
                    className="flex-1 px-4 py-3 text-sm border border-[#E2DFD8] rounded-xl focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent"
                    data-testid="showcase-compare-input"
                  />
                  <button
                    onClick={() => navigate(user ? '/app' : '/login?register=true')}
                    className="px-5 py-3 bg-[#1B3A6B] text-white rounded-xl text-sm font-medium hover:bg-[#254B8A] transition-colors whitespace-nowrap"
                    data-testid="showcase-compare-btn"
                  >
                    Try It Free
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {DEMO_COMPARE.results.map((r, i) => (
                  <div key={i} className={`flex gap-4 p-4 rounded-xl border transition-all ${r.highlight ? 'border-[#1B3A6B] bg-[#1B3A6B]/5' : 'border-[#E2DFD8] bg-[#FAFAF8]'}`} data-testid={`showcase-compare-result-${i}`}>
                    <div className={`w-2 rounded-full flex-shrink-0 ${r.highlight ? 'bg-[#1B3A6B]' : 'bg-[#E2DFD8]'}`}></div>
                    <div>
                      <p className={`text-sm font-semibold mb-1 ${r.highlight ? 'text-[#1B3A6B]' : 'text-[#0F1A2A]'}`}>{r.religion}</p>
                      <p className="text-sm text-[#475569] leading-relaxed">{r.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#94A3B8] mt-4 text-center italic">This is a preview. Sign up for full AI-powered comparisons across 6+ religions.</p>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0F1A2A]">Church Scheduling</h3>
                  <p className="text-xs text-[#94A3B8]">Organize service roles, assign members, and export branded schedules</p>
                </div>
              </div>

              <div className="flex gap-2 mb-5">
                <button onClick={() => setScheduleDay('sunday')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${scheduleDay === 'sunday' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'}`} data-testid="showcase-schedule-sunday">Sunday Service</button>
                <button onClick={() => setScheduleDay('wednesday')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${scheduleDay === 'wednesday' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'}`} data-testid="showcase-schedule-wednesday">Midweek</button>
              </div>

              <div className="rounded-xl border border-[#E2DFD8] overflow-hidden">
                <div className="bg-[#1B3A6B] px-4 py-3 flex">
                  <span className="text-xs text-white/80 font-medium w-1/3">Role</span>
                  <span className="text-xs text-white/80 font-medium w-2/3">Assigned To</span>
                </div>
                {DEMO_SCHEDULE[scheduleDay].map((item, i) => (
                  <div key={i} className={`flex px-4 py-3 ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'} border-b border-[#E2DFD8] last:border-b-0`}>
                    <span className="text-sm text-[#475569] w-1/3">{item.role}</span>
                    <span className="text-sm font-medium text-[#0F1A2A] w-2/3">{item.person}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-[#94A3B8] italic">Drag to reorder fields, save templates, export as PDF/Word</p>
                <button onClick={() => navigate(user ? '/app' : '/login?register=true')} className="px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="showcase-schedule-btn">Try Scheduling</button>
              </div>
            </div>
          )}

          {activeTab === 'scripture' && (
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0F1A2A]">Scripture Lookup</h3>
                  <p className="text-xs text-[#94A3B8]">Instantly find any verse with cross-references and Greek/Hebrew word studies</p>
                </div>
              </div>

              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={scriptureRef || 'John 3:16'}
                  onChange={e => setScriptureRef(e.target.value)}
                  placeholder="e.g., John 3:16, Psalm 23, Romans 8:28"
                  className="flex-1 px-4 py-3 text-sm border border-[#E2DFD8] rounded-xl focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent"
                  data-testid="showcase-scripture-input"
                />
                <button
                  onClick={() => navigate(user ? '/app' : '/login?register=true')}
                  className="px-5 py-3 bg-[#1B3A6B] text-white rounded-xl text-sm font-medium hover:bg-[#254B8A] whitespace-nowrap"
                  data-testid="showcase-scripture-btn"
                >
                  Look Up
                </button>
              </div>

              <div className="bg-[#FAFAF8] rounded-xl p-6 border border-[#E2DFD8]">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold text-[#1B3A6B] bg-[#1B3A6B]/10 px-2 py-0.5 rounded">John 3:16</span>
                  <span className="text-xs text-[#94A3B8]">World English Bible</span>
                </div>
                <blockquote className="text-base text-[#0F1A2A] leading-relaxed italic border-l-4 border-[#B8860B] pl-4">
                  "For God so loved the world, that he gave his one and only Son, that whoever believes in him should not perish, but have eternal life."
                </blockquote>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-[#475569] bg-white px-2.5 py-1 rounded-full border border-[#E2DFD8]">
                    <Search className="w-3 h-3" /> Word Study: agape (love)
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-[#475569] bg-white px-2.5 py-1 rounded-full border border-[#E2DFD8]">
                    Cross-ref: Romans 5:8
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-[#475569] bg-white px-2.5 py-1 rounded-full border border-[#E2DFD8]">
                    Cross-ref: 1 John 4:9
                  </span>
                </div>
              </div>
              <p className="text-xs text-[#94A3B8] mt-4 text-center italic">Sign up to access full Scripture lookup with Greek & Hebrew word studies.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function WelcomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="welcome-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E2DFD8]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-xl font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(link => (
              link.to ? (
                <Link key={link.label} to={link.to} className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-[#1B3A6B] transition-colors rounded-lg hover:bg-[#F4F2EC]">
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-[#1B3A6B] transition-colors rounded-lg hover:bg-[#F4F2EC]">
                  {link.label}
                </a>
              )
            ))}
          </div>
          
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/app')} className="px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors" data-testid="go-to-dashboard">
                Go to Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm font-medium text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg transition-colors" data-testid="nav-login">
                  Sign In
                </button>
                <button onClick={() => navigate('/login?register=true')} className="px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors" data-testid="nav-register">
                  Start Free Trial
                </button>
              </>
            )}
          </div>
          
          <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-[#E2DFD8] px-6 py-4 space-y-2">
            {NAV_LINKS.map(link => (
              link.to ? (
                <Link key={link.label} to={link.to} onClick={() => setMobileMenu(false)} className="block px-3 py-2 text-sm text-[#475569] hover:text-[#1B3A6B] rounded-lg">
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} onClick={() => setMobileMenu(false)} className="block px-3 py-2 text-sm text-[#475569] hover:text-[#1B3A6B] rounded-lg">
                  {link.label}
                </a>
              )
            ))}
            {!user && (
              <div className="pt-2 space-y-2">
                <button onClick={() => navigate('/login')} className="w-full py-2.5 text-sm font-medium text-[#1B3A6B] border border-[#1B3A6B] rounded-lg">Sign In</button>
                <button onClick={() => navigate('/login?register=true')} className="w-full py-2.5 text-sm font-medium text-white bg-[#1B3A6B] rounded-lg">Start Free Trial</button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#B8860B]/10 text-[#B8860B] text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            3-Day Free Pro Trial for New Users
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F1A2A] tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
            Enhancing Your
            <span className="block text-[#1B3A6B]">Bible Study</span>
          </h1>
          
          <p className="text-lg text-[#475569] max-w-2xl mx-auto mb-10 leading-relaxed">
            Generate professional 4-week Bible studies, devotionals, sermon notes, and curriculum in under 2 minutes. Built for pastors, teachers, and ministry leaders.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button onClick={() => navigate(user ? '/app' : '/login?register=true')} className="px-8 py-3.5 bg-[#1B3A6B] text-white rounded-xl text-base font-semibold hover:bg-[#254B8A] transition-all flex items-center gap-2 shadow-lg shadow-[#1B3A6B]/20" data-testid="hero-cta">
              {user ? 'Go to Dashboard' : 'Start Your Free Trial'}
              <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="px-6 py-3.5 text-[#1B3A6B] font-medium text-base hover:bg-[#F4F2EC] rounded-xl transition-colors">
              See How It Works
            </a>
          </div>
          
          {/* Spiritual Disclaimer - Prominent */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 shadow-sm border border-[#E2DFD8]" data-testid="spiritual-disclaimer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#B8860B]/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-[#B8860B]" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <h3 className="font-heading text-lg font-bold text-[#0F1A2A] mb-2">A Guide, Not a Replacement</h3>
                <p className="text-[#475569] leading-relaxed">
                  <strong className="text-[#0F1A2A]">This tool is designed to assist — never to replace — diligent, prayer-filled, and Spirit-led preparation.</strong> The content generated here is a starting point: a framework to spark ideas, organize thoughts, and save time on formatting. 
                  The true power of any Bible study, sermon, or devotional comes from time spent in God's Word, on your knees in prayer, and under the guidance of the Holy Spirit. 
                  Use this tool as a helper, but let your preparation always be rooted in faith and led by the Spirit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F1A2A] mb-4">Everything Your Ministry Needs</h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">Powerful tools designed specifically for churches and Christian organizations.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="p-6 rounded-2xl bg-[#F9F8F6] hover:bg-[#F4F2EC] transition-colors group">
                <div className="w-11 h-11 rounded-xl bg-[#1B3A6B]/10 flex items-center justify-center mb-4 group-hover:bg-[#1B3A6B]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-[#1B3A6B]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-bold text-[#0F1A2A] mb-2">{f.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Showcase */}
      <InteractiveShowcase navigate={navigate} user={user} />

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F1A2A] mb-4">Simple, Transparent Pricing</h2>
            <p className="text-[#475569] text-base lg:text-lg">Start with a 3-day free Pro trial. No credit card required.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {Object.entries(TIER_DATA).map(([key, tier]) => (
              <div key={key} className={`relative rounded-2xl overflow-hidden transition-transform hover:scale-[1.02] ${tier.popular ? 'ring-2 ring-[#B8860B] shadow-xl' : 'shadow-lg'}`} data-testid={`landing-tier-${key}`}>
                {tier.popular && (
                  <div className="bg-[#B8860B] text-white text-center text-xs font-bold py-1.5 uppercase tracking-wider">Most Popular</div>
                )}
                <div className={`p-6 ${key === 'pro' ? 'bg-[#1B3A6B] text-white' : key === 'church' ? 'bg-gradient-to-br from-[#1B3A6B] to-[#0F1A2A] text-white' : key === 'organization' ? 'bg-gradient-to-br from-[#B8860B] to-[#7C5800] text-white' : 'bg-[#F4F2EC]'}`}>
                  <h3 className={`text-lg font-bold mb-2 ${key === 'starter' ? 'text-[#1B3A6B]' : ''}`}>{tier.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">${tier.price}</span>
                    <span className={`text-sm ${key === 'starter' ? 'text-[#475569]' : 'text-white/70'}`}>/month</span>
                  </div>
                </div>
                <div className="bg-white p-8">
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#0F1A2A]">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate(user ? '/pricing' : '/login?register=true')} className={`w-full py-3 rounded-lg font-medium text-sm transition-all ${tier.popular ? 'bg-[#B8860B] text-white hover:bg-[#D19C11]' : 'bg-[#1B3A6B] text-white hover:bg-[#254B8A]'}`}>
                    {user ? 'Manage Plan' : 'Start Free Trial'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-[#475569] mt-8">Free tier includes 5 generations/month. All paid plans start with a 3-day Pro trial. No credit card required.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1B3A6B]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Transform Your Ministry Prep?</h2>
          <p className="text-white/70 text-lg mb-8">Join pastors and ministry leaders who are using AI as a guide to create deeper, richer Bible studies.</p>
          <button onClick={() => navigate(user ? '/app' : '/login?register=true')} className="px-8 py-3.5 bg-[#B8860B] text-white rounded-xl text-base font-semibold hover:bg-[#D19C11] transition-all shadow-lg" data-testid="bottom-cta">
            {user ? 'Go to Dashboard' : 'Start Your Free Trial'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A2A] text-white/60 py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg font-bold text-white tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
            </div>
            <p className="text-sm leading-relaxed">AI-powered tools to assist your ministry — guided by the Spirit, rooted in Scripture.</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3 text-sm">Platform</h4>
            <div className="space-y-2">
              <Link to="/about" className="block text-sm hover:text-white transition-colors">About Us</Link>
              <a href="#features" className="block text-sm hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="block text-sm hover:text-white transition-colors">Pricing</a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3 text-sm">Resources</h4>
            <div className="space-y-2">
              <Link to="/resources" className="block text-sm hover:text-white transition-colors">Free Resources</Link>
              <Link to="/guide" className="block text-sm hover:text-white transition-colors">Guide & How-To</Link>
              <Link to="/support" className="block text-sm hover:text-white transition-colors">Support</Link>
              <Link to="/contact" className="block text-sm hover:text-white transition-colors">Contact Us</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3 text-sm">Get Started</h4>
            <div className="space-y-2">
              <Link to="/login" className="block text-sm hover:text-white transition-colors">Sign In</Link>
              <Link to="/login?register=true" className="block text-sm hover:text-white transition-colors">Create Account</Link>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-8 border-t border-white/10 text-sm text-center">
          &copy; {new Date().getFullYear()} dsashun. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
